import { Paperclip } from "lucide-react";

import type { AttachmentNote, ContactNote, DealNote } from "../types";
import { useAttachmentUrl } from "./useAttachmentUrl";

/**
 * Displays persisted note attachments in note show/list views.
 *
 * This component receives a full note record and renders all attachments.
 *
 * @param props.note - Note record containing attachments to render.
 * @returns `null` when there are no attachments, otherwise attachment previews and links.
 */
export const NoteAttachments = ({ note }: { note: ContactNote | DealNote }) => {
  if (!note.attachments || note.attachments.length === 0) {
    return null;
  }

  const imageAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => isImageMimeType(attachment.type),
  );
  const otherAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => !isImageMimeType(attachment.type),
  );

  return (
    <div className="mt-2 flex flex-col gap-2">
      {imageAttachments.length > 0 && (
        // Almikia design-system.md section 8: a fixed 200px-wide grid cell
        // overflowed the 390px mobile note page; columns and images are now
        // both fluid (aspect-ratio instead of a fixed height) so the grid
        // never exceeds its container width.
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {imageAttachments.map((attachment: AttachmentNote, index: number) => (
            <ImageAttachment key={index} attachment={attachment} />
          ))}
        </div>
      )}
      {otherAttachments.length > 0 &&
        otherAttachments.map((attachment: AttachmentNote, index: number) => (
          <FileAttachment key={index} attachment={attachment} />
        ))}
    </div>
  );
};

/** Image attachment: resolved lazily to a signed URL, filename when unavailable. */
const ImageAttachment = ({ attachment }: { attachment: AttachmentNote }) => {
  const { url, status } = useAttachmentUrl(attachment.src, attachment.path);

  if (!url) {
    return (
      <span className="flex aspect-video items-center justify-center rounded-md border border-border px-2 text-center text-sm break-all">
        {status === "pending" ? null : attachment.title}
      </span>
    );
  }

  return (
    <a
      href={url}
      title={attachment.title}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-md border border-border"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={url}
        alt={attachment.title}
        className="aspect-video w-full object-cover cursor-pointer"
      />
    </a>
  );
};

/** File attachment link: signed on render, plain filename when signing fails. */
const FileAttachment = ({ attachment }: { attachment: AttachmentNote }) => {
  const { url } = useAttachmentUrl(attachment.src, attachment.path);

  if (!url) {
    return (
      <span className="-mx-2 flex min-h-11 items-center gap-2 px-2">
        <Paperclip className="w-4 h-4 shrink-0" />
        {attachment.title}
      </span>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      // The icon and label used to be split across an unlinked <div>
      // and a separate <a>, so tapping the paperclip did nothing and
      // the row fell under the 44px mobile touch-target floor; both
      // now live inside the same link.
      className="-mx-2 flex min-h-11 items-center gap-2 rounded-md px-2 underline hover:no-underline hover:bg-accent/50"
      onClick={(e) => e.stopPropagation()}
    >
      <Paperclip className="w-4 h-4 shrink-0" />
      {attachment.title}
    </a>
  );
};

/**
 * Checks whether a mime type corresponds to an image.
 *
 * @param mimeType - The attachment mime type.
 * @returns `true` when the mime type starts with `image/`.
 */
const isImageMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }
  return mimeType.startsWith("image/");
};
